import { Reorder, useDragControls } from "framer-motion";
import { type ReactNode, type RefObject, useRef } from "react";

import Icon from "@/components/Icon";

import { moveIndexForReorder } from "./careerPlanDocument";

const PLAN_ITEM_DRAG_STYLE = {
  backgroundColor: "var(--sys-brand-surface-container)",
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
  opacity: 0.8,
  border: "1px solid var(--sys-brand-outline-variant)",
  borderRadius: "16px",
  zIndex: 1000
};

type SortablePlanItemsProps<T extends { id: string }> = {
  fields: T[];
  onMove: (from: number, to: number) => void;
  onDragStart?: () => void;
  dragTestId: (index: number) => string;
  children: (field: T, index: number, dragHandle: ReactNode) => ReactNode;
};

export default function SortablePlanItems<T extends { id: string }>({
  fields,
  onMove,
  onDragStart,
  dragTestId,
  children
}: SortablePlanItemsProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const ids = fields.map((field) => field.id);
  const canDrag = fields.length > 1;

  const startDrag = () => {
    onDragStart?.();
  };

  return (
    <div ref={containerRef}>
      <Reorder.Group
        axis="y"
        values={ids}
        onReorder={(nextIds) => {
          const indexes = moveIndexForReorder(ids, nextIds);
          if (!indexes) return;
          startDrag();
          onMove(indexes.from, indexes.to);
        }}
        layoutScroll
        className="m-0 flex list-none flex-col gap-4 p-0"
        style={{ overflow: "visible", width: "100%" }}
      >
        {fields.map((field, index) => (
          <SortablePlanItem
            key={field.id}
            id={field.id}
            canDrag={canDrag}
            containerRef={containerRef}
            dragTestId={dragTestId(index)}
            onDragStart={startDrag}
          >
            {(dragHandle) => children(field, index, dragHandle)}
          </SortablePlanItem>
        ))}
      </Reorder.Group>
    </div>
  );
}

function SortablePlanItem({
  id,
  canDrag,
  containerRef,
  dragTestId,
  onDragStart,
  children
}: {
  id: string;
  canDrag: boolean;
  containerRef: RefObject<HTMLDivElement | null>;
  dragTestId: string;
  onDragStart: () => void;
  children: (dragHandle: ReactNode) => ReactNode;
}) {
  const dragControls = useDragControls();
  const dragHandle = canDrag ? (
    <div
      className="drag-handle flex h-6 w-6 shrink-0 cursor-grab items-center justify-center"
      data-testid={dragTestId}
      aria-hidden="true"
      onPointerDown={(event) => {
        onDragStart();
        dragControls.start(event);
      }}
      style={{ touchAction: "none" }}
    >
      <Icon name="drag_indicator" />
    </div>
  ) : null;

  return (
    <Reorder.Item
      value={id}
      dragControls={dragControls}
      dragListener={false}
      drag={canDrag}
      layout
      transition={{ duration: 0 }}
      onDragStart={onDragStart}
      dragConstraints={containerRef}
      dragElastic={0.1}
      whileDrag={PLAN_ITEM_DRAG_STYLE}
      style={{ position: "relative" }}
    >
      {children(dragHandle)}
    </Reorder.Item>
  );
}
